import React, { useState, useEffect, useCallback } from "react";
import { Collapse } from "react-bootstrap";
import "./assets/style/Pricing.css";
import { useLocationStore } from "./hooks/useLocationStore";

const CollapsedListElem = (props) => {
  const [isVisible, initHs] = useState(false);
  const invokeCollapse = () => initHs(!isVisible);

  const colSize = props.passes.length <= 4 ? Math.floor(12 / props.passes.length) : 3;
  const colClass = `col-lg-${colSize}`;

  return (
    <li>
      <a onClick={invokeCollapse}>
        {props.title}
        <i className="bx bx-chevron-down icon-show"></i>
        <i className="bx bx-chevron-up icon-close"></i>
      </a>
      <Collapse in={isVisible}>
        <div className="collapse" data-bs-parent=".accordion-list">
          <div className="row pricing-elem">
            {props.passes.map((pass, index) => (
              <div
                className={colClass}
                data-aos="fade-up"
                data-aos-delay={`${(index + 1) * 100}`}
                key={index}
              >
                <div className={`box ${pass.featured ? 'featured' : ''}`}>
                  <h3>{pass.title}</h3>
                  <h4>
                    {pass.price}
                    <sup>zł</sup>
                  </h4>
                  {pass.duration && (
                    <p className="duration-info">{pass.duration}</p>
                  )}
                </div>
              </div>
            ))}
            {props.additional && (
              <div className="col-lg-12">
                <p>{props.additional}</p>
              </div>
            )}
          </div>
        </div>
      </Collapse>
    </li>
  );
};

const Pricing = () => {
  const [priceCategories, setPriceCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selectedLocationId } = useLocationStore();

  const fetchPriceCategories = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_BACKEND_ADDRESS}/price-list-categories/`;
      if (selectedLocationId) {
        url += `?location_id=${selectedLocationId}`;
      }
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch price categories: ${response.status}`);
      }
      
      const data = await response.json();
      setPriceCategories(data.price_list_categories || []);
    } catch (error) {
      console.error('Error fetching price categories:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedLocationId]);

  useEffect(() => {
    fetchPriceCategories();
  }, [fetchPriceCategories]);

  const formatDuration = (validDuration) => {
    if (!validDuration) return null;
    
    // validDuration comes as seconds from the backend
    const days = Math.floor(validDuration / 86400); // 86400 seconds in a day
    const weeks = Math.floor(days / 7);
    
    if (days === 30) return "Ważny przez miesiąc";
    if (days === 90) return "Ważny przez 3 miesiące";
    if (days === 365) return "Ważny przez rok";
    
    // Display in weeks if duration is a multiple of 7 days
    if (days % 7 === 0 && weeks > 0) {
      if (weeks === 1) return "Ważny przez tydzień";
      if (weeks === 2) return "Ważny przez 2 tygodnie";
      if (weeks === 3) return "Ważny przez 3 tygodnie";
      if (weeks === 4) return "Ważny przez 4 tygodnie";
      return `Ważny przez ${weeks} tygodni`;
    }
    
    // Fallback to days for non-week durations
    return `Ważny przez ${days} dni`;
  };

  const formatPriceList = (category) => {
    const passes = [];
    
    // Add single lesson entry if available
    if (category.single_price) {
      passes.push({
        title: "Wejście jednorazowe",
        price: category.single_price.toString(),
        duration: null,
      });
    }
    
    // Add pass types
    category.pass_types.forEach(passType => {
      passes.push({
        id: passType.id,
        title: passType.name,
        price: passType.price.toString(),
        duration: formatDuration(passType.valid_duration),
        featured: passType.is_promoted || false,
      });
    });
    
    return passes;
  };

  if (loading) {
    return (
      <section id="pricing" className="pricing">
        <div className="container" data-aos="fade-up">
          <div className="section-title">
            <h2>Cennik</h2>
          </div>
          <div className="row">
            <div className="col-lg-12 pt-4 pt-lg-0 content">
              <p>Ładowanie cennika...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="pricing" className="pricing">
        <div className="container" data-aos="fade-up">
          <div className="section-title">
            <h2>Cennik</h2>
          </div>
          <div className="row">
            <div className="col-lg-12 pt-4 pt-lg-0 content">
              <p>Błąd podczas ładowania cennika. Spróbuj ponownie później.</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" className="pricing">
      <div className="container" data-aos="fade-up">
        <div className="section-title">
          <h2>Cennik</h2>
        </div>
        <div className="row">
          <div
            className="col-lg-12 pt-4 pt-lg-0 content"
            data-aos="fade-left"
            data-aos-delay="100"
          >
            <div className="accordion-list">
              <ul>
                {priceCategories.map((category, index) => (
                  <CollapsedListElem
                    key={category.id}
                    categoryId={category.id}
                    title={category.name}
                    passes={formatPriceList(category)}
                    additional={category.additional_info}
                  />
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
